/**
 * Truncated gateway cell for stats table.
 * Shows Voluum gateway ID (gatewayId_routeId) and route name (from smswiser_country_route).
 * Hover: ID, Name, Tags (StatsHoverPopover). For Media Devoted | Created By Ciro Autiero
 */

import type { FC } from 'react';

import { LeadsListTags } from '@/features/smswiser-campaigns/components/shared';
import { useToast } from '@/providers/ToastProvider';

import {
  StatsHoverPopover,
  StatsTooltipSection,
  StatsTooltipTagsBlock,
} from '../../../StatsHoverPopover';
import { truncateEnd } from '../../../shared';

interface TruncatedGatewayCellProps {
  /** Voluum gateway ID (e.g. 64_1103) */
  gatewayId?: string | null;
  /** Gateway/route display name (from smswiser_country_route.name) */
  label: string;
  /** Performance tags from gateway-performance-tags API */
  performanceTags?: string[];
  /** Tag + date range for hover tooltip */
  performanceTagDetails?: Array<{ tag: string; dateRangePreset: string }>;
  /** Custom colors for tags (rule name -> hex) */
  tagColors?: Record<string, string>;
}

export const TruncatedGatewayCell: FC<TruncatedGatewayCellProps> = ({
  gatewayId,
  label,
  performanceTags,
  performanceTagDetails,
  tagColors,
}) => {
  const { success: toastSuccess, error: toastError } = useToast();
  const displayLabel = label || gatewayId || '—';
  const truncatedLabel = truncateEnd(displayLabel, 32);

  const handleCopyId = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!gatewayId) return;
    navigator.clipboard
      .writeText(gatewayId)
      .then(() => toastSuccess('Gateway ID copied', 'Copied to clipboard'))
      .catch(() => toastError('Copy failed', 'Could not copy to clipboard'));
  };

  const handleCopyLabel = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const text = truncatedLabel.full || displayLabel;
    if (!text || text === '—') return;
    navigator.clipboard
      .writeText(text)
      .then(() => toastSuccess('Route name copied', 'Copied to clipboard'))
      .catch(() => toastError('Copy failed', 'Could not copy to clipboard'));
  };

  const tagsLine = (performanceTags?.length ?? 0) > 0 ? (
    <LeadsListTags
      performanceTags={performanceTags ?? []}
      tagColors={tagColors}
      className="inline-flex ml-1"
      compact
      disableTooltip
    />
  ) : null;

  const hoverContent = (
    <div className="flex flex-col gap-1.5 text-left">
      <StatsTooltipSection label="ID" valueClassName="font-mono">
        {gatewayId || '—'}
      </StatsTooltipSection>
      <StatsTooltipSection label="Name" valueClassName="font-medium">
        {truncatedLabel.full || displayLabel || '—'}
      </StatsTooltipSection>
      {((performanceTagDetails?.length ?? performanceTags?.length) ?? 0) > 0 && (
        <div>
          <StatsTooltipTagsBlock
            performanceTags={performanceTags}
            performanceTagDetails={performanceTagDetails}
            tagColors={tagColors}
          />
        </div>
      )}
    </div>
  );

  return (
    <StatsHoverPopover content={hoverContent}>
      <div className="flex min-w-0 items-baseline gap-1.5 text-xs text-muted-foreground cursor-default">
        <span
          className="inline-flex shrink-0 w-[8ch] font-mono tabular-nums"
          aria-hidden
        >
          <button
            type="button"
            onClick={handleCopyId}
            title="Click to copy gateway ID"
            className="font-medium text-foreground cursor-pointer rounded px-0.5 -mx-0.5 hover:bg-muted/60 hover:text-primary disabled:pointer-events-none disabled:opacity-50"
            aria-label="Copy gateway ID"
            disabled={!gatewayId}
          >
            {gatewayId || '—'}
          </button>
        </span>
        <button
          type="button"
          onClick={handleCopyLabel}
          title="Click to copy route name"
          className="min-w-0 truncate text-left text-inherit bg-transparent border-0 p-0 cursor-pointer rounded px-0.5 -mx-0.5 hover:bg-muted/60 hover:text-primary font-medium text-foreground/80"
          aria-label="Copy route name"
        >
          {truncatedLabel.display}
        </button>
        {tagsLine}
      </div>
    </StatsHoverPopover>
  );
};
